import React from 'react';
import { StyleSheet, View } from 'react-native';

import PlanAvatarStack from '@components/Plan/AvatarStack';
import PlanCommentButton from '@components/Plan/CommentButton';
import PlanDetails from '@components/Plan/Details';
import type {
  PlanDetailsProps,
  PlanGroupProfile,
  PlanParticipantProfile,
} from '@components/Plan/Details';
import PlanJoinButton from '@components/Plan/JoinButton';
import PlanLikeButton from '@components/Plan/LikeButton';
import PlanMessageButton from '@components/Plan/MessageButton';
import { PlanShareButton } from '@components/Plan/ShareButton';
import { PressableOpacity } from '@components/Pressable';
import ScrollView from '@components/ScrollView';
import { Row } from '@components/View';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import type { Plan } from '@types';

const DEFAULT_AVATAR_LIMIT = 4;
const DEFAULT_AVATAR_SIZE = 50;

type PlanSummaryProps = Omit<React.ComponentProps<typeof View>, 'children'> & {
  /** Style applied to each action button container. */
  actionButtonStyle?: React.ComponentProps<typeof View>['style'];
  /** Content container style for the horizontal action row. */
  actionContentContainerStyle?: React.ComponentProps<typeof ScrollView>['contentContainerStyle'];
  /** Maximum number of participant avatar tiles shown in the avatar bubble. */
  avatarLimit?: number;
  /** Diameter of the participant avatar bubble. */
  avatarSize?: number;
  /** Icon size used by plan detail rows. */
  detailIconSize?: PlanDetailsProps['detailIconSize'];
  /** Maximum number of lines for each plan detail row. */
  detailTextNumberOfLines?: PlanDetailsProps['detailTextNumberOfLines'];
  /** Text style applied to plan detail rows. */
  detailTextStyle?: PlanDetailsProps['detailTextStyle'];
  /** Container style applied to the plan details block. */
  detailsStyle?: PlanDetailsProps['style'];
  /** Groups associated with the plan. */
  groupProfiles?: PlanGroupProfile[];
  /** Style applied to the content column beside the avatar bubble. */
  infoContainerStyle?: React.ComponentProps<typeof View>['style'];
  /** Called when a participant avatar is pressed. */
  onAvatarPress?: (userId: string) => void;
  /** Called when the message action resolves a chat id. */
  onChat: (chatId: string) => void;
  /** Called when the comment action is pressed. */
  onCommentPress: () => void;
  /** Called when the whole summary body is pressed. */
  onPress?: () => void;
  /** Loaded participant profiles for rendering names and avatars. */
  participantUsers?: PlanParticipantProfile[];
  /** Plan represented by this summary. */
  plan: Plan;
  /** Style applied to the avatar/details row. */
  rowStyle?: React.ComponentProps<typeof Row>['style'];
  /** Whether to render the comment action button. */
  showCommentAction?: boolean;
};

export default function PlanSummary({
  actionButtonStyle,
  actionContentContainerStyle,
  avatarLimit = DEFAULT_AVATAR_LIMIT,
  avatarSize = DEFAULT_AVATAR_SIZE,
  detailIconSize,
  detailTextNumberOfLines,
  detailTextStyle,
  detailsStyle,
  groupProfiles,
  infoContainerStyle,
  onAvatarPress,
  onChat,
  onCommentPress,
  onPress,
  participantUsers = [],
  plan,
  rowStyle,
  showCommentAction,
  style,
  ...props
}: PlanSummaryProps) {
  const content = (
    <>
      <Row style={[styles.row, rowStyle]}>
        <PlanAvatarStack
          avatarLimit={avatarLimit}
          avatarSize={avatarSize}
          onAvatarPress={onAvatarPress}
          participantUsers={participantUsers}
          plan={plan}
        />
        <View style={[styles.infoContainer, infoContainerStyle]}>
          <PlanDetails
            detailIconSize={detailIconSize}
            detailTextNumberOfLines={detailTextNumberOfLines}
            detailTextStyle={detailTextStyle}
            groupProfiles={groupProfiles}
            participantUsers={participantUsers}
            plan={plan}
            style={detailsStyle}
          />
        </View>
      </Row>
      <PlanActionRow
        actionButtonStyle={actionButtonStyle}
        actionContentContainerStyle={actionContentContainerStyle}
        onChat={onChat}
        onCommentPress={onCommentPress}
        plan={plan}
        showCommentAction={showCommentAction}
      />
    </>
  );

  return (
    <View style={style} {...props}>
      {onPress ? <PressableOpacity onPress={onPress}>{content}</PressableOpacity> : content}
    </View>
  );
}

type PlanActionRowProps = {
  /** Style applied to each action button container. */
  actionButtonStyle?: React.ComponentProps<typeof View>['style'];
  /** Content container style for the horizontal action row. */
  actionContentContainerStyle?: React.ComponentProps<typeof ScrollView>['contentContainerStyle'];
  /** Called when the message action resolves a chat id. */
  onChat: (chatId: string) => void;
  /** Called when the comment action is pressed. */
  onCommentPress?: () => void;
  /** Plan used by action buttons. */
  plan: Plan;
  /** Whether to render the comment action button. */
  showCommentAction?: boolean;
};

export function PlanActionRow({
  actionButtonStyle,
  actionContentContainerStyle,
  onChat,
  onCommentPress,
  plan,
  showCommentAction = true,
}: PlanActionRowProps) {
  const currentUserId = getCurrentUserId();
  const isCurrentUser = plan.userId === currentUserId;
  const participantUserIds = new Set([plan.userId, ...(plan.userIds || [])]);
  const showMessageAction = !isCurrentUser || participantUserIds.size > 1;

  return (
    <ScrollView
      horizontal
      contentContainerStyle={[styles.actionScrollContentContainer, actionContentContainerStyle]}>
      <View style={[styles.actionButtonContainer, actionButtonStyle]}>
        <PlanLikeButton planDoc={plan} />
      </View>
      {!isCurrentUser ? (
        <View style={[styles.actionButtonContainer, actionButtonStyle]}>
          <PlanJoinButton planDoc={plan} />
        </View>
      ) : null}
      {showMessageAction ? (
        <View style={[styles.actionButtonContainer, actionButtonStyle]}>
          <PlanMessageButton onChat={onChat} plan={plan} />
        </View>
      ) : null}
      {showCommentAction && onCommentPress ? (
        <View style={[styles.actionButtonContainer, actionButtonStyle]}>
          <PlanCommentButton planDoc={plan} onPress={onCommentPress} />
        </View>
      ) : null}
      <View style={[styles.actionButtonContainer, actionButtonStyle]}>
        <PlanShareButton planDoc={plan} sendingUserId={currentUserId} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  actionButtonContainer: {
    marginBottom: 5,
    marginHorizontal: 2,
  },
  actionScrollContentContainer: {
    paddingHorizontal: 5,
  },
  infoContainer: {
    flex: 1,
    marginBottom: 5,
    marginHorizontal: 5,
    minWidth: 0,
  },
  row: {
    alignItems: 'center',
  },
});
